'use client';

import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { ReviewStar as Icon_ReviewStar } from "@/components/customer/icons/ReviewStar";
const SvgIcons = { ReviewStar: Icon_ReviewStar };
import { notFound } from 'next/navigation';
import sanitizeHtml from 'sanitize-html';
import { getImageUrl } from '@/lib/imageUrl';
import GlobalPartnersCount from '@/components/GlobalPartnersCount';

// Lazy load heavy/client-only components – now with SSR enabled
const FaqSection = dynamic(() => import('../FaqSection'), { ssr: true });
const InterCustomerSlider = dynamic(() => import('../InterCustomerSlider'), { ssr: true });
const TestiReviewCommonImages = dynamic(() => import('./TestiReviewCommonImages'), { ssr: true });
const AnimatedCommonCardContent = dynamic(() => import('./AnimatedCommonCardContent'), { ssr: true });
const PagesTestimonials = dynamic(() => import('./PagesTestimonials'), { ssr: true });

// Sanitize configuration - Full SVG support
const sanitizeOptions: sanitizeHtml.IOptions = {
  allowedTags: [
    ...sanitizeHtml.defaults.allowedTags,
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'div', 'span', 'section', 'article', 'aside', 'header', 'footer', 'main',
    'figure', 'figcaption', 'p', 'br', 'hr',
    'strong', 'em', 'b', 'i', 'u', 's',
    'ul', 'ol', 'li', 'a', 'blockquote', 'pre', 'code',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'img', 'iframe', 'video', 'source', 'picture',
    // SVG — ALL lowercase (sanitize-html lowercases everything)
    'svg', 'g', 'defs', 'symbol', 'use', 'clippath', 'mask', 'pattern', 'marker',
    'path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon',
    'text', 'tspan', 'textpath',
    'lineargradient', 'radialgradient', 'stop',
    'filter', 'feblend', 'fecolormatrix', 'fecomposite', 'feconvolvematrix',
    'fediffuselighting', 'fedisplacementmap', 'fedropshadow', 'feflood',
    'fegaussianblur', 'feimage', 'femerge', 'femergenode', 'femorphology',
    'feoffset', 'fespecularlighting', 'fetile', 'feturbulence',
    'fepointlight', 'fespotlight', 'fedistantlight',
    'animate', 'animatetransform', 'animatemotion', 'mpath', 'set',
    'image', 'view', 'desc', 'title', 'metadata', 'switch', 'foreignobject',
  ],

  allowedAttributes: {
    '*': [
      'class', 'style', 'id', 'lang', 'dir',
      'aria-label', 'aria-hidden', 'aria-describedby', 'aria-labelledby',
      'role', 'tabindex',
      // SVG presentation attributes
      'fill', 'fill-opacity', 'fill-rule', 'clip-rule',
      'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
      'stroke-dasharray', 'stroke-dashoffset', 'stroke-opacity',
      'opacity', 'transform', 'visibility', 'display',
      'clip-path', 'color', 'font-family', 'font-size', 'font-style', 'font-weight',
      'stop-color', 'stop-opacity', 'text-anchor', 'text-decoration',
      'flood-color', 'flood-opacity', 'lighting-color',
      'marker-end', 'marker-mid', 'marker-start',
      'overflow', 'pointer-events', 'cursor', 'vector-effect',
      'paint-order', 'shape-rendering', 'text-rendering', 'image-rendering',
      'color-interpolation', 'color-interpolation-filters',
      'filter', 'mask', 'letter-spacing', 'word-spacing',
      'unicode-bidi', 'writing-mode',
    ],

    a: ['href', 'target', 'rel', 'title', 'download'],
    img: ['src', 'alt', 'width', 'height', 'loading', 'srcset', 'sizes', 'decoding', 'class', 'style'],
    iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'loading', 'title', 'referrerpolicy', 'sandbox'],
    th: ['scope', 'colspan', 'rowspan'],
    td: ['colspan', 'rowspan'],

    svg: ['id', 'xmlns', 'width', 'height', 'viewbox', 'preserveaspectratio', 'x', 'y', 'version', 'fill', 'class', 'style'],
    g: ['id', 'transform'],
    defs: ['id'],
    use: ['id', 'href', 'x', 'y', 'width', 'height'],
    symbol: ['id', 'viewbox', 'preserveaspectratio', 'x', 'y', 'width', 'height'],

    // ✅ lowercase keys + id included
    clippath: ['id', 'clippathunits'],
    mask: ['id', 'maskunits', 'maskcontentunits', 'x', 'y', 'width', 'height'],
    pattern: ['id', 'x', 'y', 'width', 'height', 'patternunits', 'patterncontentunits', 'patterntransform', 'viewbox', 'preserveaspectratio'],
    marker: ['id', 'markerwidth', 'markerheight', 'markerunits', 'refx', 'refy', 'orient', 'viewbox', 'preserveaspectratio'],

    path: ['id', 'd', 'pathlength', 'fill-rule', 'clip-rule'],
    circle: ['id', 'cx', 'cy', 'r', 'pathlength'],
    ellipse: ['id', 'cx', 'cy', 'rx', 'ry', 'pathlength'],
    rect: ['id', 'x', 'y', 'width', 'height', 'rx', 'ry', 'pathlength'],
    line: ['id', 'x1', 'y1', 'x2', 'y2', 'pathlength'],
    polyline: ['id', 'points', 'pathlength'],
    polygon: ['id', 'points', 'pathlength'],

    // ✅ Gradients with lowercase keys — THIS was the core bug
    lineargradient: ['id', 'x1', 'y1', 'x2', 'y2', 'gradientunits', 'gradienttransform', 'spreadmethod', 'href'],
    radialgradient: ['id', 'cx', 'cy', 'r', 'fx', 'fy', 'fr', 'gradientunits', 'gradienttransform', 'spreadmethod', 'href'],
    stop: ['id', 'offset', 'stop-color', 'stop-opacity'],

    filter: ['id', 'x', 'y', 'width', 'height', 'filterunits', 'primitiveunits', 'color-interpolation-filters'],
    fegaussianblur: ['id', 'stddeviation', 'in', 'result', 'edgemode'],
    feoffset: ['id', 'dx', 'dy', 'in', 'result'],
    feblend: ['id', 'mode', 'in', 'in2', 'result'],
    feflood: ['id', 'flood-color', 'flood-opacity', 'result'],
    fecomposite: ['id', 'operator', 'in', 'in2', 'k1', 'k2', 'k3', 'k4', 'result'],
    fecolormatrix: ['id', 'type', 'values', 'in', 'result'],
    femerge: ['id', 'result'],
    femergenode: ['id', 'in'],
    fedropshadow: ['id', 'dx', 'dy', 'stddeviation', 'flood-color', 'flood-opacity', 'in', 'result'],
    feimage: ['id', 'href', 'preserveaspectratio', 'result'],
    feturbulence: ['id', 'type', 'basefrequency', 'numoctaves', 'seed', 'stitchtiles', 'result'],
    femorphology: ['id', 'operator', 'radius', 'in', 'result'],
    fedisplacementmap: ['id', 'in', 'in2', 'scale', 'xchannelselector', 'ychannelselector', 'result'],
    fediffuselighting: ['id', 'surfacescale', 'diffuseconstant', 'in', 'result'],
    fespecularlighting: ['id', 'surfacescale', 'specularconstant', 'specularexponent', 'in', 'result'],
    fepointlight: ['id', 'x', 'y', 'z'],
    fespotlight: ['id', 'x', 'y', 'z', 'pointsatx', 'pointsaty', 'pointsatz', 'specularexponent', 'limitingconeangle'],
    fedistantlight: ['id', 'azimuth', 'elevation'],
    fetile: ['id', 'in', 'result'],
    feconvolvematrix: ['id', 'order', 'kernelmatrix', 'divisor', 'bias', 'targetx', 'targety', 'edgemode', 'preservealpha', 'in', 'result'],

    text: ['id', 'x', 'y', 'dx', 'dy', 'rotate', 'textlength', 'lengthadjust'],
    tspan: ['id', 'x', 'y', 'dx', 'dy', 'rotate', 'textlength', 'lengthadjust'],
    textpath: ['id', 'href', 'startoffset', 'method', 'spacing', 'textlength', 'lengthadjust'],
    image: ['id', 'href', 'x', 'y', 'width', 'height', 'preserveaspectratio', 'decoding'],

    animate: ['id', 'attributename', 'values', 'dur', 'begin', 'end', 'repeatcount', 'fill', 'calcmode', 'keytimes', 'keysplines', 'from', 'to', 'by', 'additive', 'accumulate'],
    animatetransform: ['id', 'attributename', 'type', 'values', 'dur', 'begin', 'end', 'repeatcount', 'fill', 'calcmode', 'keytimes', 'from', 'to', 'by', 'additive', 'accumulate'],
    animatemotion: ['id', 'path', 'dur', 'begin', 'end', 'repeatcount', 'fill', 'calcmode', 'keytimes', 'keypoints', 'rotate', 'origin'],
    set: ['id', 'attributename', 'to', 'begin', 'end', 'dur', 'fill'],
    foreignobject: ['id', 'x', 'y', 'width', 'height'],
  },

  // NOTE: No transformTags here — and that is intentional.
  // sanitize-html lowercases every tag/attribute name. Renaming tags back to
  // camelCase (e.g. lineargradient -> linearGradient) via transformTags runs
  // BEFORE attribute filtering, so the lowercase allow-list keys below no longer
  // match the renamed tag and ALL its attributes get stripped — including the
  // gradient `id`, which breaks `fill="url(#paint0_...)"` and makes the SVG
  // invisible. Instead we keep everything lowercase; the browser's HTML parser
  // re-cases SVG elements/attributes (lineargradient -> linearGradient,
  // viewbox -> viewBox, gradientunits -> gradientUnits, ...) automatically when
  // the markup is injected via dangerouslySetInnerHTML.

  allowedSchemes: ['http', 'https', 'mailto', 'tel', 'data'],
  allowedSchemesByTag: {
    img: ['http', 'https', 'data'],
    image: ['http', 'https', 'data'],
    a: ['http', 'https', 'mailto', 'tel'],
    iframe: ['http', 'https'],
  },
  allowedIframeHostnames: ['www.youtube.com', 'player.vimeo.com', 'www.loom.com', 'calendly.com'],
  disallowedTagsMode: 'discard',
  enforceHtmlBoundary: false,
};


const SafeHtml = ({ html, keepFirstH1 = false }: { html: string | null; keepFirstH1?: boolean }) => {
  if (!html || html.trim() === '') return null;

  let cleanHtml = sanitizeHtml(html, sanitizeOptions);

  // Normalize CMS headings so a page body can't yield duplicate <h1>s. Keep only the first <h1>
  // (when keepFirstH1) as the page's sole heading and demote every other <h1> to <h2>. Headings
  // don't nest, so opening/closing tags alternate 1:1 and can be counted independently.
  {
    let opens = 0;
    let closes = 0;
    cleanHtml = cleanHtml.replace(/<(\/?)h1((?:\s[^>]*)?)>/gi, (full: string, slash: string, attrs: string) => {
      if (slash) { closes++; return keepFirstH1 && closes === 1 ? '</h1>' : '</h2>'; }
      opens++; return keepFirstH1 && opens === 1 ? full : `<h2${attrs}>`;
    });
  }

  if (process.env.NODE_ENV === 'development') {
    // Find exactly which tags are being stripped
    const rawTags = html.match(/<([a-zA-Z][a-zA-Z0-9]*)/g)?.map(t => t.slice(1).toLowerCase()) ?? [];
    const cleanTags = cleanHtml.match(/<([a-zA-Z][a-zA-Z0-9]*)/g)?.map(t => t.slice(1).toLowerCase()) ?? [];

    const rawCount: Record<string, number> = {};
    const cleanCount: Record<string, number> = {};

    rawTags.forEach(t => rawCount[t] = (rawCount[t] || 0) + 1);
    cleanTags.forEach(t => cleanCount[t] = (cleanCount[t] || 0) + 1);

    const stripped: Record<string, { raw: number; clean: number; lost: number }> = {};
    for (const tag of new Set([...Object.keys(rawCount), ...Object.keys(cleanCount)])) {
      const raw = rawCount[tag] || 0;
      const clean = cleanCount[tag] || 0;
      if (raw !== clean) {
        stripped[tag] = { raw, clean, lost: raw - clean };
      }
    }

    // Also log which SVG-specific attributes are being lost
    const gradientRaw = (html.match(/id="paint[^"]*"/g) || []).length;
    const gradientClean = (cleanHtml.match(/id="paint[^"]*"/g) || []).length;
    if (gradientRaw !== gradientClean) {
      console.warn(`SafeHtml — gradient IDs: raw=${gradientRaw}, clean=${gradientClean} (${gradientRaw - gradientClean} lost)`);
    }
  }

  return (
    <div
      className="prose prose-lg max-w-none [&_svg]:max-w-full [&_svg]:h-auto [&_svg]:inline-block"
      dangerouslySetInnerHTML={{ __html: cleanHtml ?? '' }}
    />
  );
};



const CommonCard = ({
  pageslug,
  content,
  content1,
  faqs,
}: {
  pageslug: string;
  content: string | null;
  content1: string | null;
  faqs: any[] | null;
}) => {
  // 404 if no content
  if (!content || content.trim() === '') {
    notFound();
  }


  // Validate FAQs
  const hasValidFaqs =
    faqs &&
    faqs.length > 0 &&
    faqs.some(
      (faq) =>
        faq &&
        typeof faq === 'object' &&
        faq.question &&
        faq.answer &&
        faq.question.trim() !== '' &&
        faq.answer.trim() !== ''
    );

  // Visible breadcrumb matching the BreadcrumbList schema (Home > Solutions > … > page).
  return (
    <div className="min-h-screen">
      {/* Main Content — keep its first <h1> as the page's sole heading */}
      <SafeHtml html={content} keepFirstH1 />

      {/* Trusted Partners Slider */}
      <div className="overflow-x-hidden bg-white">
        <section className="relative py-[32px] md:py-[60px]">
          <AnimatedCommonCardContent
            headerText={<>Trusted by <GlobalPartnersCount />+ global partners</>}
            headerClass="text-[#090D28] text-center font-Schibsted_Grotesk text-[16px] md:text-lg font-normal leading-[120%]"
          >
            <div className="relative overflow-hidden">
              <InterCustomerSlider />
              <div className="pointer-events-none absolute inset-y-0 left-0 w-[80px] md:w-[280px] bg-gradient-to-r from-white to-transparent z-10" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-[80px] md:w-[280px] bg-gradient-to-l from-white to-transparent z-10" />
            </div>
          </AnimatedCommonCardContent>
        </section>
      </div>

      {/* Secondary Content */}
      <SafeHtml html={content1} />

      {/* Testimonials Section */}
      <section className="bg-[#01001E]">
        <section className="py-[60px] md:py-[120px] px-4 md:px-0 md:container mx-auto">
          <AnimatedCommonCardContent
            headerText="We Make for Your Team"
            headerClass="text-white text-center text-[32px] md:text-[48px] font-extrabold leading-[120%] font-NeueMontreal"
          >
            <p className="text-[#c4c4c4] text-center text-[16px] md:text-[18px] font-normal leading-[150%] font-Schibsted_Grotesk py-4 max-w-4xl mx-auto">
              Our team ensures timely results without compromising quality
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mt-8">
              <TestiReviewCommonImages />
              <div className="text-center md:text-left">
                <a
                  href="https://maps.app.goo.gl/wxZr7ckDhrWnVbx27"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white hover:underline"
                >
                  <Image src={getImageUrl("/vilvabusiness/website/common/homepage/google-img.webp")} alt="" width={24} height={24} className="w-6 h-6" />
                  <span className="text-sm font-LatinkaRegular">Google Reviews</span>
                </a>
                <div className="flex items-center justify-center md:justify-start gap-2 mt-2 text-white">
                  <span className="text-lg font-bold font-Schibsted_Grotesk">4.7</span>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <SvgIcons.ReviewStar key={i} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 md:mt-20">
              <PagesTestimonials />
            </div>
          </AnimatedCommonCardContent>
        </section>
      </section>

      {/* FAQ Section */}
      {hasValidFaqs && <FaqSection faqs={faqs!} />}
    </div>
  );
};

export default CommonCard;